declare const jobData: any; // from localized JSON_small file
import * as JobTypes from "../types/jobData";
import { JobOverviewPage } from "./jobOverviewPage";

/**
 * Handles the active filters and filters the jobs accordingly to the active filters
 */
/* NOTE - be sure, that workingTimes & targetGroups & categories 
are set as data-category-group in the sidebar on .dt-filter-sidebar__category  */
export class FilterStateManager {
  private activeFilters: Record<string, Set<string>> = {};
  private jobOverviewPageInstance: JobOverviewPage;
  // openState: Record<UpperCategory, boolean> ---handles - open filters state from upper category -
  private openStates: Record<string, boolean> = {
    workingTimes: false,
    jobLevel: false,
    jobOpening: false,
  };

  private filterCallback: (jobs: JobTypes.jobData[]) => void = () => {};

  constructor(jobOverviewPageInstance: JobOverviewPage) {
    this.jobOverviewPageInstance = jobOverviewPageInstance;
    this.setEvents();
  }

  public getOpenState(categoryGroup: string): boolean {
    return this.openStates[categoryGroup] ?? false; // default: false
  }

  public setOpenState(categoryGroup: string, state: boolean) {
    this.openStates[categoryGroup] = state;
  }

  onFilterChange(callback: (jobs: JobTypes.jobData[]) => void) {
    this.filterCallback = callback;
  }

  performFilter() {
    const foundJobs = this.filterByActiveFilters(this.jobOverviewPageInstance.getAllJobs);
    this.filterCallback(foundJobs);
  }

  // For extra Filtering (e.g. on SearchChange Trigger)
  performFilterAndReturn(jobs: JobTypes.jobData[]): JobTypes.jobData[] {
    const foundJobs = this.filterByActiveFilters(jobs);
    return foundJobs;
  }

  // TODO: Implement Filter Logic

  /**
   * Itterates over all jobs and checks if the job fits the active filters
   *
   * @returns - found jobs[]
   */
  public filterByActiveFilters(jobs: JobTypes.jobData[]): JobTypes.jobData[] {
    const foundJobs = jobs.filter((job: JobTypes.jobData) => {
      const workingFilter = this.checkJobFilters(job);
      const jobLevelFilter = this.checkJobLevelFilters(job);
      const jobOpeningFilter = this.checkJobOpeningFilters(job);

      return workingFilter && jobLevelFilter && jobOpeningFilter; //
    });
    return foundJobs;
  }

  // Working Times - Condition
  private checkJobFilters(job: JobTypes.jobData) {
    if (this.activeFilters["workingTimes"]?.size) {
      return job.jobOpening.workingTimes.some((workingTime) =>
        this.activeFilters["workingTimes"].has(workingTime.name)
      );
    }

    return true; // No Active Filter = show all
  }

  // jobLevel - Condition
  private checkJobLevelFilters(job: JobTypes.jobData) {
    if (this.activeFilters["jobLevel"]?.size) {
      return job.jobOpening.targetGroups.some((targetGroups) => this.activeFilters["jobLevel"].has(targetGroups.name));
    }

    return true; // No Active Filter = show all
  }

  private checkJobOpeningFilters(job: JobTypes.jobData) {
    if (this.activeFilters["jobOpening"]?.size) {
      return job.jobOpening.categories.some((category) => this.activeFilters["jobOpening"].has(category.name));
    }

    return true;
  }

  /**
   * Fires when a checkbox is clicked
   */
  private setEvents() {
    document.addEventListener("change", (event: Event) => {
      const target = event.target as HTMLElement;

      if (target.classList.contains("dt-filter-sidebar__checkbox-row-input")) {
        this.handleCheckboxClick(target);
      }
    });
  }

  /**
   * Fires when a checkbox is clicked
   */
  private handleCheckboxClick(target: HTMLElement) {
    const categoryGroup = target.closest(".dt-filter-sidebar__category") as HTMLElement;
    const checkbox = target.closest(".dt-filter-sidebar__checkbox-row");

    if (!checkbox || !categoryGroup) return;

    const input = checkbox.querySelector(".dt-filter-sidebar__checkbox-row-input") as HTMLInputElement;
    const label = checkbox.querySelector("label") as HTMLElement;

    // get datas
    let categoryGroupKey = categoryGroup.dataset.categoryGroup as string;
    const identifierName = label.textContent as string;

    if (input.checked) {
      this.addActiveFilter(categoryGroupKey, identifierName);
    } else {
      this.removeActiveFilter(categoryGroupKey, identifierName);
    }

    this.performFilter();
  }

  private addActiveFilter(categoryGroup: string, identifier: string) {
    if (!this.activeFilters[categoryGroup]) {
      this.activeFilters[categoryGroup] = new Set();
    }
    this.activeFilters[categoryGroup].add(identifier);
  }

  public removeActiveFilter(categoryGroup: string, identifier: string) {
    if (!this.activeFilters[categoryGroup]) return; // nothing to remove
    this.activeFilters[categoryGroup].delete(identifier);
  }

  public isAFilterActive(): boolean {
    return Object.values(this.activeFilters).some((filters) => filters.size > 0);
  }

  public getActiveFilters() {
    return this.activeFilters;
  }
}
